'use client';

import { useEffect, useState } from 'react';

import type { PageBlock } from '@/payload/payload-types-block';
import { useRequireAuth } from '@/store/user';
import { UiGiftCardCreate } from '@/ui/gift-card-create';
import { AccordionItem } from '@/ui-kit/accordion';
import { BreadcrumbsItem } from '@/ui-kit/breadcrumbs';
import { useForm } from '@/ui-tool/form';

import { formAction } from './api/formAction';

type GiftCardCreateDesignCard = NonNullable<
  PageBlock<'giftCardCreate'>['designCards']
>[number];

const getCardImageUrl = (card: GiftCardCreateDesignCard) => {
  const imageUrl =
    typeof card.image === 'object' && card.image !== null ? card.image.url : '';

  if (typeof window === 'undefined') {
    return imageUrl;
  }

  if (!imageUrl || imageUrl.startsWith('http')) {
    return imageUrl;
  }

  return window.location.origin + imageUrl;
};

export interface GiftCardCreateProps {
  giftCards: PageBlock<'giftCardCreate'>['designCards'];
  policyLink: string;
  breadcrumbs: BreadcrumbsItem[];
  faq?: AccordionItem[];
  initialRecipientProfile?: {
    phone?: string;
    email?: string;
  } | null;
}

export function GiftCardCreate({
  giftCards,
  policyLink,
  breadcrumbs,
  faq,
  initialRecipientProfile
}: GiftCardCreateProps) {
  const requireAuth = useRequireAuth();
  const [selectedCard, setSelectedCard] = useState<GiftCardCreateDesignCard | null>(
    giftCards[0] || null
  );
  const [quantity, setQuantity] = useState(1);
  const [nominal, setNominal] = useState<number>(0);

  const { getFieldProps, action, pending, state, errors } = useForm(formAction);

  useEffect(() => {
    if (state.status !== 'success') {
      return;
    }

    const paymentLink = state.responseData?.paymentLink as string;

    if (!paymentLink) {
      return;
    }

    window.location.assign(paymentLink);
  }, [state.responseData?.paymentLink, state.status]);

  if (!giftCards.length || !selectedCard) {
    return null;
  }

  const selectedCardImageUrl = getCardImageUrl(selectedCard);

  if (!selectedCardImageUrl) {
    return null;
  }

  return (
    <form
      noValidate
      action={action}
      onSubmit={(event) => {
        if (requireAuth()) return;
        event.preventDefault();
      }}
    >
      <input
        type="hidden"
        name="giftCardId"
        value={selectedCard.id || ''}
      />
      <input
        type="hidden"
        name="giftCardImageUrl"
        value={selectedCardImageUrl}
      />
      <input
        type="hidden"
        name="nominal"
        value={nominal}
      />
      <input
        type="hidden"
        name="quantity"
        value={quantity}
      />
      <UiGiftCardCreate
        giftCards={giftCards}
        selectedCard={selectedCard}
        onChangeSelectedCard={setSelectedCard}
        policyLink={policyLink}
        breadcrumbs={breadcrumbs}
        faq={faq}
        quantity={quantity}
        nominal={nominal}
        onChangeQuantity={setQuantity}
        onChangeNominal={setNominal}
        getFieldProps={getFieldProps}
        initialRecipientProfile={initialRecipientProfile}
        isPayButtonDisabled={pending}
        nominalError={
          errors?.nominal
            ? Array.isArray(errors.nominal)
              ? errors.nominal[0]
              : errors.nominal
            : undefined
        }
      />
    </form>
  );
}
